import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ABOUT_IMAGE, ABOUT_TEXTURE, STATS } from "../data/content";

export default function About() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [-80, 80]);
    const textY = useTransform(scrollYProgress, [0, 1], [80, -40]);

    return (
        <section
            id="about"
            ref={ref}
            data-testid="about-section"
            className="relative bg-[#0a0908] py-28 sm:py-40 overflow-hidden"
        >
            {/* Background ornate texture */}
            <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                    backgroundImage: `url(${ABOUT_TEXTURE})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />

            <div className="relative max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
                {/* Image side */}
                <motion.div
                    style={{ y: imgY }}
                    className="lg:col-span-5 relative"
                >
                    <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                            src={ABOUT_IMAGE}
                            alt="Adibah - Master Makeup Artist"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0908]/40 to-transparent" />
                    </div>
                    {/* Floating frame */}
                    <div className="absolute -bottom-6 -right-6 w-2/3 h-2/3 border border-[#d4af37]/50 -z-0 hidden sm:block" />
                    {/* Floating signature plate */}
                    <div className="absolute -left-4 sm:-left-10 top-10 bg-[#0a0908] border border-[#d4af37]/30 px-6 py-4 z-10">
                        <p className="text-[10px] tracking-[0.4em] uppercase text-[#d4af37] mb-1">
                            Professional MUA
                        </p>
                        <p className="font-display text-2xl text-[#fdfbf7]">
                            Lina wati
                        </p>
                    </div>
                </motion.div>

                {/* Text side */}
                <motion.div
                    style={{ y: textY }}
                    className="lg:col-span-7"
                >
                    <div className="eyebrow mb-6">Sebuah Mahakarya</div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#fdfbf7] font-light leading-[1.05] tracking-tight">
                        Makeup Artist
                        <span className="block italic text-[#d4af37]">
                            Glamour Natural
                        </span>
                        untuk hari istimewamu.
                    </h2>

                    <div className="mt-8 max-w-xl space-y-5 text-[#a6a196] leading-relaxed">
                        <p>
                            Hallo, saya <strong className="text-[#fdfbf7]">Lina wati</strong>, seorang Makeup Artist (MUA) profesional yang berbasis di kawasan Bekasi, Karawang dan Jakarta. Kecantikan bagi saya adalah kanvas untuk mengekspresikan karakter dan kepribadian setiap individu.
                        </p>
                        <p>
                            Memulai perjalanan karier sejak <strong className="text-[#fdfbf7]">2017</strong>, saya telah menangani berbagai macam kebutuhan riasan — mulai dari <em>flawless wedding</em>, akad, lamaran, wisuda, bridesmaid, keluarga, party, hingga editorial makeup. Dengan sentuhan yang <strong className="text-[#fdfbf7]">glamour</strong> namun tetap <strong className="text-[#fdfbf7]">natural</strong>, komitmen saya adalah menonjolkan kecantikan alami setiap klien agar mereka tampil percaya diri di hari istimewa.
                        </p>
                        <p className="text-[#d4af37]">
                            ✦ Signature Glam Makeup ✦
                        </p>
                    </div>

                    {/* Contact Info */}
                    <div className="mt-6 flex items-center gap-2 text-[#a6a196]">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37]">Email</span>
                        <a
                            href="mailto:lw5051900@gmail.com"
                            className="text-[#fdfbf7] hover:text-[#d4af37] transition-colors duration-300 underline underline-offset-4 decoration-[#d4af37]/30"
                        >
                            lw5051900@gmail.com
                        </a>
                    </div>

                    {/* Stats */}
                    <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-[#d4af37]/15 pt-10">
                        {STATS.map((s, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.7 }}
                                data-testid={`stat-${i}`}
                            >
                                <p className="font-display text-3xl sm:text-4xl text-[#d4af37] mb-1">
                                    {s.value}
                                </p>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-[#a6a196]">
                                    {s.label}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
